/**
 * Sözleşmeler (Satın Alma) — Şantiye Seçim Ekranı (Faz 34.60)
 * Kullanıcı önce şantiye kartını seçer, sonra o şantiyenin sözleşmelerini görür.
 * DeliveryNotesIndex / SubProgressIndex ile aynı desen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { searchMatchTR } from "@/lib/textFormat";
import { toast } from "sonner";
import {
  MapPin, MagnifyingGlass, CaretRight, FileText, CheckCircle, ListChecks,
} from "@phosphor-icons/react";

export default function ContractsIndex() {
  const { sites } = useSites();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contracts`);
        setRows(data || []);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const bySite = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const sid = r.site_id;
      if (!sid) continue;
      if (!map[sid]) map[sid] = { count: 0, active: 0 };
      map[sid].count += 1;
      if (r.status === "aktif") map[sid].active += 1;
    }
    return map;
  }, [rows]);

  const visible = useMemo(() => {
    const active = (sites || []).filter((s) => s.status !== "arsiv");
    if (!q.trim()) return active;
    return active.filter((s) =>
      searchMatchTR(q, [s.code, s.name, s.city, s.district].filter(Boolean).join(" "))
    );
  }, [sites, q]);

  return (
    <Shell>
      <div className="space-y-6" data-testid="contracts-index-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#1B4776]/10 flex items-center justify-center">
              <FileText size={22} weight="duotone" className="text-[#1B4776]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1B4776]">Sözleşmeler</h1>
              <p className="text-sm text-slate-500">
                Sözleşmeleri yönetmek için önce bir şantiye seçin.
              </p>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Şantiye ara..." className="pl-9 w-72"
              data-testid="contracts-index-search" />
          </div>
        </div>

        {visible.length === 0 ? (
          <Card className="p-10 text-center text-slate-500">
            {q ? "Arama sonucu bulunamadı." : "Henüz aktif şantiye bulunmuyor."}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visible.map((s) => {
              const c = bySite[s.id] || { count: 0, active: 0 };
              return (
                <Link key={s.id} to={`/sozlesmeler/${s.id}`}
                      data-testid={`contracts-index-site-${s.id}`} className="group">
                  <Card className="p-5 hover:border-[#1B4776] hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-[#1B4776]/40 hover:border-l-[#1B4776]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono font-bold text-[#1B4776] tracking-wider uppercase">
                          {s.code}
                        </div>
                        <div className="mt-1 text-base font-bold text-[#0F172A] group-hover:text-[#1B4776] transition-colors line-clamp-2">
                          {s.name}
                        </div>
                        {(s.city || s.district) && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                            <MapPin size={13} weight="fill" className="text-[#1B4776]/60" />
                            <span>{[s.district, s.city].filter(Boolean).join(", ")}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-2 rounded-full bg-slate-50 group-hover:bg-[#1B4776] transition-colors">
                        <CaretRight size={16} weight="bold"
                          className="text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px]">
                      <div className="inline-flex items-center gap-1.5 text-[#1B4776]" title="Toplam Sözleşme">
                        <ListChecks size={13} weight="bold" />
                        <span className="font-bold">{c.count}</span>
                        <span className="uppercase tracking-widest">Sözleşme</span>
                      </div>
                      {c.active > 0 && (
                        <div className="inline-flex items-center gap-1.5 text-emerald-700" title="Aktif">
                          <CheckCircle size={13} weight="bold" />
                          <span className="font-bold">{c.active}</span>
                          <span className="uppercase tracking-widest">Aktif</span>
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
